import { ContentPromotionForm } from "@/types/models/ContentPromotionForm.interface";
import { License } from "@/types/models";

import { UserClassroom } from "@/classrooms/queries";
import { User as UserV3 } from "@/types/models/User/v3/User.interface";
import { SubjectProductSlug } from "../types";

const DEFAULT_GRADE_LEVEL = 8;

const getUserGradeLevels = (classrooms: UserClassroom[]) => {
  const gradeLevels = classrooms.reduce(
    (gradeLevels: number[], classroom: UserClassroom) => {
      if (!gradeLevels.includes(classroom?.grade)) {
        gradeLevels.push(classroom?.grade);
      }

      return gradeLevels;
    },
    [],
  );

  return gradeLevels;
};

interface ParamsToDetermineValidGradeRange {
  form: ContentPromotionForm;
  classrooms: UserClassroom[];
}

const isValidGradeRange = ({
  form,
  classrooms,
}: ParamsToDetermineValidGradeRange) => {
  // Fetch the user's classrooms to identify the grade-levels that they teach.
  const userGradeLevels = getUserGradeLevels(classrooms) ?? [];
  if (userGradeLevels.length === 0) {
    userGradeLevels?.push(DEFAULT_GRADE_LEVEL);
  }

  for (const gradeLevel of userGradeLevels) {
    if (gradeLevel >= form.grade_minimum && gradeLevel <= form.grade_maximum)
      return true;
  }
  return false;
};

interface ParamsToDetermineValidLicenses {
  form: ContentPromotionForm;
  licenses: License[];
}

const isValidSubjectLicense = ({
  licenses,
  form,
}: ParamsToDetermineValidLicenses) => {
  /**
   *  Science and SS forms require an exact product license,
   *  all of other product licenses are valid for ELA forms.
   */
  for (const license of licenses) {
    if (license.is_active) {
      if (
        form.product_slug === SubjectProductSlug.science ||
        form.product_slug === SubjectProductSlug.social_studies
      ) {
        if (form.product_slug === license.product) return true;
      } else {
        return true;
      }
    }
  }
  return false;
};

interface ParamsToDetermineFormAccess {
  user: UserV3;
  form: ContentPromotionForm;
  licenses: License[] | undefined;
  classrooms: UserClassroom[] | undefined;
}

const canUserAccessContentPromotionForm = ({
  user,
  licenses,
  form,
  classrooms,
}: ParamsToDetermineFormAccess) => {
  if (!user) return false;

  const isAuthorizedDistrictAdmin = user.roles.includes("district_admin");
  const isAuthorizedSchoolAdmin = user.roles.includes("school_admin");
  const isAuthorizedTeacher = user.roles.includes("teacher");

  // Admins can see all content-promotion forms that exist in their venue.
  if (isAuthorizedSchoolAdmin || isAuthorizedDistrictAdmin) return true;

  if (!form || !licenses || !classrooms) return false;

  // Teachers can only see forms that were created for the grade-level and
  // subject-products (licenses) that they teach.
  if (isAuthorizedTeacher) {
    return (
      isValidGradeRange({ form, classrooms }) &&
      isValidSubjectLicense({ licenses, form })
    );
  }
  return false;
};

export default canUserAccessContentPromotionForm;
